/**
 * lbmgb-track.js — Avenor agency tracking JavaScript module.
 *
 * Vendored per brand. Public API on `window.LBMGB.track`.
 *
 * Contract:
 *   - LBMGB.track.* are the events the storefront calls (e.g. on add-to-cart click).
 *   - All events go to GTM dataLayer with a UUID `event_id` attached for browser↔server CAPI dedup.
 *   - identify({ email, phone }) hashes client-side (SHA-256, normalized) before pushing to dataLayer.
 *     Hashed values flow to GA4, Meta CAPI advanced matching, Google Enhanced Conversions automatically
 *     via the GTM golden container variable mappings.
 *   - Klaviyo dual-call: identify() ALSO pushes to _learnq if Klaviyo's onsite JS is loaded.
 *     No-op if Klaviyo not present (per ADR-4.9 deferral).
 *   - Consent-gated: events buffer in memory until the consent layer grants analytics_storage.
 *
 * Per ADRs 4.2 (Web Pixel sister), 4.8 (identity), 4.9 (Klaviyo), 4.10 (PII).
 *
 * Configuration via window.LBMGB.config (set by lbmgb-tracking.liquid):
 *   - sgtmEndpoint: string (required)
 *   - gtmWebContainerId: string (required, "GTM-XXXXXXX")
 *   - cookieDomain: string (required per ADR-4.8 — host-only or subdomain-scoped per region)
 *   - defaultCountryCode: string ("US"|"CA"|...) for phone normalization
 *   - debug: boolean (mirrors events to console)
 */
(function () {
  'use strict';

  // -------------------------------------------------------------------------
  // Setup
  // -------------------------------------------------------------------------

  var LBMGB = (window.LBMGB = window.LBMGB || {});
  var config = LBMGB.config || {};

  if (!config.sgtmEndpoint) {
    if (config.debug) console.warn('[LBMGB] Missing sgtmEndpoint config; tracking disabled.');
    return; // No-op gracefully if theme not configured (per ADR-4.15 vendor-then-configure).
  }

  // Guard against double-init.
  if (LBMGB.track) return;

  // -------------------------------------------------------------------------
  // Buffer + consent state
  // -------------------------------------------------------------------------

  var buffered = []; // { name, params, timestamp } — drained on consent grant
  var consented = false; // analytics_storage + ad_user_data both granted
  var sessionUserData = null; // hashed user_data populated by identify()

  // dataLayer is GTM's interface — push to it after consent only.
  window.dataLayer = window.dataLayer || [];

  function debug() {
    if (config.debug) {
      // eslint-disable-next-line no-console
      console.log.apply(console, ['[LBMGB]'].concat(Array.prototype.slice.call(arguments)));
    }
  }

  // -------------------------------------------------------------------------
  // UUID helper
  // -------------------------------------------------------------------------

  function uuid() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    // Fallback for older browsers.
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      var v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  // -------------------------------------------------------------------------
  // SHA-256 hashing (Web Crypto API)
  // -------------------------------------------------------------------------

  function sha256Hex(input) {
    if (!input) return null;
    var encoder = new TextEncoder();
    var data = encoder.encode(String(input));
    return crypto.subtle.digest('SHA-256', data).then(function (hashBuffer) {
      var hashArray = Array.prototype.slice.call(new Uint8Array(hashBuffer));
      return hashArray
        .map(function (b) {
          return b.toString(16).padStart(2, '0');
        })
        .join('');
    });
  }

  function normalizeEmail(email) {
    if (!email) return null;
    return String(email).trim().toLowerCase();
  }

  function normalizePhone(phone) {
    if (!phone) return null;
    var digits = String(phone).replace(/[^\d+]/g, '');
    var country = (config.defaultCountryCode || 'US').toUpperCase();
    // Already E.164-prefixed
    if (digits.charAt(0) === '+') return digits;
    // 10 digits with country=US/CA → assume +1
    if ((country === 'US' || country === 'CA') && digits.length === 10) {
      return '+1' + digits;
    }
    // 11 digits starting with 1, US/CA → assume +1
    if ((country === 'US' || country === 'CA') && digits.length === 11 && digits.charAt(0) === '1') {
      return '+' + digits;
    }
    // Other locales: warn (libphonenumber recommended per ADR-4.8) but try +-prefix anyway
    if (config.debug) {
      console.warn('[LBMGB] Phone normalization fallback for non-US/CA — consider opting into libphonenumber');
    }
    return '+' + digits;
  }

  function normalizeName(name) {
    if (!name) return null;
    return String(name).trim().toLowerCase();
  }

  // -------------------------------------------------------------------------
  // Internal: push an event to dataLayer (or buffer if not consented)
  // -------------------------------------------------------------------------

  /* Page context for every event.
     GA4 reads page_location / page_path off the event that triggers the tag.
     This theme is a Shopify multi-page site with no page_view push in the
     dataLayer, so custom events (add_to_cart, emailSignup, view_item) arrived
     with no page fields and GA4 fell back to "/" with an unset session source.
     Verified live on 8 Sep 2026: all 52 add_to_cart events in the US property
     were recorded on page "/" with sessionSource "(not set)", which made every
     landing page read as zero add-to-carts.

     page_location is stripped of click-id and campaign parameters so landing
     pages aggregate into one row instead of one row per click. fbclid alone
     produced 375 of 400 distinct landing-page rows. The click id is still
     captured for attribution by the first-party pixel and the Meta _fbc
     cookie, so nothing is lost by removing it here. */
  var STRIP_PARAMS = [
    'fbclid', 'gclid', 'wbraid', 'gbraid', 'msclkid', 'ttclid', 'twclid',
    'igshid', 'mc_eid', 'mc_cid', '_ga', 'campaign_id', 'adset_id', 'ad_id',
    'ad_name', 'adset_name', 'campaign_name', 'placement', 'site_source_name'
  ];

  function cleanUrl(href) {
    try {
      var u = new URL(href || window.location.href);
      STRIP_PARAMS.forEach(function (p) { u.searchParams.delete(p); });
      return u.toString();
    } catch (_) {
      return href || '';
    }
  }

  function pageFields() {
    try {
      var clean = cleanUrl(window.location.href);
      var u = new URL(clean);
      return {
        page_location: clean,
        page_path: u.pathname,
        page_title: document.title,
        page_referrer: document.referrer || undefined
      };
    } catch (_) {
      return {};
    }
  }

  function pushEvent(name, params) {
    var event = Object.assign({
      event: name,
      event_id: uuid(),
      timestamp: Date.now()
    }, pageFields(), params || {});

    // Attach session-wide hashed user_data on every event after identify().
    if (sessionUserData) {
      event.user_data = Object.assign({}, sessionUserData);
    }

    if (!consented) {
      buffered.push(event);
      debug('Buffered (no consent yet):', name);
      return;
    }

    window.dataLayer.push(event);
    debug('Pushed to dataLayer:', name, event);
  }

  // -------------------------------------------------------------------------
  // Public API
  // -------------------------------------------------------------------------

  LBMGB.track = {
    /**
     * Identify the visitor with email/phone (and optional address fields).
     * Hashes client-side per ADR-4.8 + 4.10. Stored as session user_data,
     * attached to every subsequent event. Also forwards raw values to Klaviyo
     * if `_learnq` is present (per ADR-4.9 dual-call).
     */
    identify: async function (params) {
      params = params || {};
      var hashedUserData = {};

      if (params.email) {
        var em = normalizeEmail(params.email);
        if (em) hashedUserData.em = await sha256Hex(em);
      }
      if (params.phone) {
        var ph = normalizePhone(params.phone);
        if (ph) hashedUserData.ph = await sha256Hex(ph);
      }
      if (params.firstName) {
        hashedUserData.fn = await sha256Hex(normalizeName(params.firstName));
      }
      if (params.lastName) {
        hashedUserData.ln = await sha256Hex(normalizeName(params.lastName));
      }
      if (params.city) hashedUserData.ct = await sha256Hex(normalizeName(params.city));
      if (params.state) hashedUserData.st = await sha256Hex(normalizeName(params.state));
      if (params.zip) hashedUserData.zp = await sha256Hex(String(params.zip).trim());
      if (params.country) hashedUserData.country = String(params.country).toUpperCase().trim();

      sessionUserData = hashedUserData;
      debug('Identified (session hashed user_data set):', Object.keys(hashedUserData));

      // Klaviyo dual-call (ADR-4.9). No-op if Klaviyo onsite JS not loaded.
      // Callers that identify while an address is still being typed pass
      // `klaviyo: false`: a Klaviyo identify creates a profile for every
      // intermediate string, and on 8 Sep 2026 one visitor produced six
      // typo profiles that Klaviyo then synced into Shopify as customers.
      if (window._learnq && params.email && params.klaviyo !== false) {
        try {
          window._learnq.push([
            'identify',
            {
              $email: params.email,
              $phone_number: params.phone || undefined,
              $first_name: params.firstName || undefined,
              $last_name: params.lastName || undefined
            }
          ]);
          debug('Klaviyo identify pushed (raw values to Klaviyo only)');
        } catch (e) {
          // Klaviyo not installed or errored — silently no-op.
        }
      }
    },

    pageView: function (params) {
      pushEvent('page_view', params);
    },

    viewItem: function (params) {
      pushEvent('view_item', { ecommerce: params });
    },

    viewItemList: function (params) {
      pushEvent('view_item_list', { ecommerce: params });
    },

    selectItem: function (params) {
      pushEvent('select_item', { ecommerce: params });
    },

    addToCart: function (params) {
      pushEvent('add_to_cart', { ecommerce: params });
    },

    removeFromCart: function (params) {
      pushEvent('remove_from_cart', { ecommerce: params });
    },

    viewCart: function (params) {
      pushEvent('view_cart', { ecommerce: params });
    },

    beginCheckout: function (params) {
      pushEvent('begin_checkout', { ecommerce: params });
    },

    addPaymentInfo: function (params) {
      pushEvent('add_payment_info', { ecommerce: params });
    },

    addShippingInfo: function (params) {
      pushEvent('add_shipping_info', { ecommerce: params });
    },

    /**
     * Purchase event. The `transaction_id` should be the Shopify order ID
     * to dedup against the Web Pixel's checkout-side fire (per ADR-4.2).
     */
    purchase: function (params) {
      pushEvent('purchase', { ecommerce: params });
    },

    generateLead: function (params) {
      pushEvent('generate_lead', params);
    },

    /**
     * Custom: emailSignup (per ADR-4.2 custom event taxonomy).
     */
    emailSignup: function (params) {
      pushEvent('emailSignup', params);
    },

    /**
     * Custom: partnerRedirect (links to partner sites — track outbound).
     */
    partnerRedirect: function (params) {
      pushEvent('partnerRedirect', params);
    },

    /**
     * Escape hatch: arbitrary custom event.
     */
    custom: function (eventName, params) {
      pushEvent(eventName, params);
    }
  };

  // -------------------------------------------------------------------------
  // Consent integration
  // -------------------------------------------------------------------------

  /**
   * Called by the consent layer when consent state changes. The bridge in
   * snippets/lbmgb-tracking.liquid translates Shopify Customer Privacy
   * events into grant/revoke calls here (per ADR-4.21).
   */
  LBMGB.consent = {
    grant: function () {
      if (consented) return;
      consented = true;
      debug('Consent granted; flushing buffered events:', buffered.length);

      // Flush buffered events in order.
      var toFlush = buffered.splice(0);
      for (var i = 0; i < toFlush.length; i++) {
        window.dataLayer.push(toFlush[i]);
      }
    },

    revoke: function () {
      consented = false;
      sessionUserData = null;
      buffered = [];
      debug('Consent revoked; sessionUserData cleared, buffer dropped.');
      // Note: actually deleting cookies is the consent layer's responsibility
      // (Shopify Customer Privacy clears its own cookies on revoke); we just
      // clear our in-memory state here.
    }
  };

  debug('lbmgb-track.js loaded. Awaiting consent or initial pageView.');
})();
